// Events
import {
  ACTION_DIALOG_CLOSE,
  COLLECTION_ACTIVATE_BACK_TO_TOP,
  COLLECTION_DEACTIVATE_BACK_TO_TOP,
  COLLECTION_FILTER_UPDATE,
  COLLECTION_GRID_APPEND,
  COLLECTION_GRID_LOADING,
  COLLECTION_GRID_PREPEND,
  COLLECTION_GRID_RENDER,
  COLLECTION_GRID_UPDATE,
  COLLECTION_NEXT_PAGE,
  COLLECTION_PAGINATION_UPDATE,
  COLLECTION_PREVIOUS_PAGE,
  COLLECTION_PRODUCT_CLICK,
  COLLECTION_RUN_BACK_TO_TOP,
  COLLECTION_SUBMIT_FILTER,
  COLLECTION_UPDATE,
  COLLECTION_UPDATE_STYLES,
} from 'events';
// Lib
import ScrollYObserver from 'lib/ScrollYObserver';
import LZString from 'lz-string';
import { component } from 'picoapp';

import { setCSSProperty } from 'utils/css';

// Utils
import { parseJSONScript, stringToNode } from 'utils/node';

import { dispatch } from '../../utils/theme';

const fetchCollection = async (query) => {
  const queryString = new URLSearchParams(query);

  // Set the template to 'collection.products'
  queryString.set('view', 'products');

  try {
    const response = await fetch(`?${queryString.toString()}`);
    const text = await response.text();

    const [script, body] = stringToNode(text, ['#filter-data', '#filter-result']);

    const meta = parseJSONScript(script[0]);
    const products = [...body[0].children];

    return Promise.resolve({ meta, products });
  } catch (err) {
    return Promise.reject(err);
  }
};

const updateCurrentUrl = ({ pagination, activeFilters }, page = 0) => {
  const queryString = new URLSearchParams();

  if (pagination?.currentPage > 1) {
    queryString.set('page', page > 0 ? page : pagination.currentPage);
  }

  if (activeFilters.length) {
    activeFilters.forEach(([key, value]) => {
      queryString.append(key, value);
    });
  }

  window.history.pushState(
    {},
    null,
    [...queryString].length > 0 ? `?${queryString.toString()}` : window.location.pathname,
  );
};

const cacheState = ({ pagination, products }) => {
  const storageKey = `SNT_COLLECTION:${window.theme.collectionHandle}`;
  const gridProductsString = products.map((product) => product.outerHTML).join('');
  const compressedData = LZString.compress(
    JSON.stringify({ products: gridProductsString, pagination }),
  );
  const cachedCollections = sessionStorage.getItem('SNT_COLLECTIONS');

  try {
    sessionStorage.setItem(storageKey, compressedData);
    sessionStorage.setItem(
      'SNT_COLLECTIONS',
      cachedCollections
        ? [...new Set(cachedCollections.split(',').concat([window.theme.collectionHandle]))].join(
            ',',
          )
        : window.theme.collectionHandle,
    );
  } catch {
    if (cachedCollections) {
      cachedCollections.split(',').forEach((collectionHandle) => {
        sessionStorage.removeItem(`SNT_COLLECTION:${collectionHandle}`);
      });
      sessionStorage.removeItem('SNT_COLLECTIONS');
    }
  }
};

export default component(async (node, ctx) => {
  // Sections
  const productItem = node.querySelector('[data-collection-grid] [data-product-item-container]');

  const productCountElements = [...document.querySelectorAll('[data-collection-products-count]')];
  const backToTopMinY = (productItem?.scrollHeight || 650) * 3 + (productItem?.offsetTop || 500);

  ctx.on(COLLECTION_PRODUCT_CLICK, (state, data) => {
    if (data.page) updateCurrentUrl(state, data.page);
  });

  ctx.on(COLLECTION_SUBMIT_FILTER, async (state, formElement) => {
    const formData = new FormData(formElement);
    const activeFilters = [...formData];

    if (activeFilters.length > 0) {
      activeFilters.push(['filter.v.availability', '1']);
      formData.append('filter.v.availability', '1');
    }

    const defaultSort = formElement.querySelector('[data-default-sort-by]');

    dispatch('filter-apply', {
      size: formData.getAll('filter.v.option.size').join(','),
      fit: formData.getAll('filter.p.m.data.fit').join(','),
      color: formData.getAll('filter.p.m.data.colors').join(','),
      sort: defaultSort?.value !== formData.get('sort_by') ? formData.get('sort_by') : null,
    });

    ctx.emit([
      ACTION_DIALOG_CLOSE,
      COLLECTION_GRID_LOADING,
      COLLECTION_RUN_BACK_TO_TOP,
      COLLECTION_DEACTIVATE_BACK_TO_TOP,
    ]);

    ctx.emit(COLLECTION_GRID_UPDATE, { activeFilters });

    const queryString = new URLSearchParams(formData);

    try {
      const { meta, products } = await fetchCollection(queryString);

      ctx.emit([COLLECTION_GRID_RENDER, COLLECTION_UPDATE], { ...meta }, products);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  });

  ctx.on(COLLECTION_PREVIOUS_PAGE, async ({ pagination, activeFilters }) => {
    if (!pagination.previousPage) return;

    const queryString = new URLSearchParams(pagination.previousPage.split('?').pop());

    try {
      const { meta, products } = await fetchCollection(queryString);

      const paginationObject = pagination;
      paginationObject.previousPage = meta.pagination.previousPage;

      ctx.emit(
        [COLLECTION_GRID_PREPEND, COLLECTION_UPDATE],
        { ...meta, pagination: paginationObject },
        products,
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  });

  ctx.on(COLLECTION_NEXT_PAGE, async ({ pagination, activeFilters }) => {
    if (!pagination.nextPage) return;

    const queryString = new URLSearchParams(pagination.nextPage.split('?').pop());
    try {
      const { meta, products } = await fetchCollection(queryString);

      ctx.emit([COLLECTION_GRID_APPEND, COLLECTION_UPDATE], { ...meta }, products);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  });

  ctx.on(COLLECTION_UPDATE_STYLES, ({ pagination }) => {
    const { currentPage, pageSize, items } = pagination;

    setCSSProperty(null, '--products-count', Math.min(currentPage * pageSize, items));
    setCSSProperty(null, '--products-total', items);
  });

  ctx.on(COLLECTION_UPDATE, (state) => {
    updateCurrentUrl(state);
    cacheState({
      ...state,
      products: [
        ...node.querySelectorAll(
          '[data-grid] [data-product-item-container],[data-grid] [data-banner]',
        ),
      ],
    });

    ctx.emit([
      COLLECTION_UPDATE_STYLES,
      COLLECTION_FILTER_UPDATE,
      COLLECTION_GRID_UPDATE,
      COLLECTION_PAGINATION_UPDATE,
    ]);
  });

  ctx.on(COLLECTION_FILTER_UPDATE, ({ total }) => {
    if (typeof total !== 'number' || productCountElements.length === 0) return;

    productCountElements.forEach((el) => {
      el.textContent = total;
    });
  });

  // Init
  setTimeout(() => {
    ctx.emit([COLLECTION_UPDATE_STYLES, COLLECTION_GRID_UPDATE, COLLECTION_PAGINATION_UPDATE]);
  }, 0);

  const scrollYObserver = new ScrollYObserver();

  scrollYObserver.observe(
    ({ reachedTop }) => {
      ctx.emit(reachedTop ? COLLECTION_ACTIVATE_BACK_TO_TOP : COLLECTION_DEACTIVATE_BACK_TO_TOP);
    },
    {
      upThreshold: 75,
      downThreshold: 50,
      minY: backToTopMinY,
    },
  );

  scrollYObserver.start();

  const handleResize = () => {
    ctx.emit(COLLECTION_UPDATE_STYLES);
  };

  document.addEventListener('theme:screen:size-updated', handleResize);

  const initFilterBar = () => {
    const filterBar = node.querySelector('.filter-bar');
    const STICKY_CLASS = 'filter-bar--sticky';
    const PADDING = 12;

    const update = () => {
      const topString = getComputedStyle(filterBar).getPropertyValue('top');
      const top = parseInt(topString, 10);

      filterBar.style.position = 'static';

      const staticTop = filterBar.getBoundingClientRect().top + window.scrollY - top;

      filterBar.style.position = '';
      filterBar.classList.toggle(STICKY_CLASS, window.scrollY >= staticTop - PADDING);
    }

    document.addEventListener('scroll', update);
  };

  initFilterBar();

});
